<template>
  <div>
    <!-- LIST VIEW — same table UI as plannerSheet / plannerListTable -->
    <template v-if="activeView === 'list'">
      <b-card class="planner-list-card">
        <div class="gradient-line-wrapper"></div>

        <div
          class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
        >
          <div>
            <h5 class="mb-0 resource-title">Withdrawal Requests</h5>
            <small class="text-muted"
              >Manage withdrawal requests</small
            >
          </div>
        </div>

        <div
          class="pagination-controls-top d-flex justify-content-between align-items-center mb-2"
        >
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <div class="page-size-wrapper">
              <label class="page-size-label">Show:</label>
              <v-select
                v-model="perPage"
                :options="pageSizeOptions"
                :reduce="(option) => option.value"
                label="label"
                :clearable="false"
                :searchable="false"
                append-to-body
                class="page-size-select-compact"
                @input="onPageSizeChange"
              />
            </div>
          </div>

          <div class="pagination-count-display">
            <span class="count-text">
              Showing <strong>{{ displayedRange.start }}</strong> to
              <strong>{{ displayedRange.end }}</strong> of
              <strong>{{ totalCount }}</strong>
            </span>
          </div>

          <div class="pagination-wrapper-compact">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalCount"
              :per-page="perPage"
              size="sm"
              align="center"
              class="pagination-compact mb-0"
              @input="reload(false)"
            />
          </div>
        </div>

        <b-row class="align-items-center filter-row mb-2">
          <b-col cols="12" class="filter-toolbar d-flex align-items-center">
            <div
              class="filter-fields-group filter-fields-group--cols-4 d-flex align-items-center flex-grow-1"
            >
              <v-select
                v-model="filters.classID"
                :options="classOptions"
                :clearable="true"
                label="name"
                :reduce="(val) => val.id"
                placeholder="Class"
                append-to-body
                class="filter-control filter-field-item"
                @input="onFilterClassChange"
              />

              <div class="filter-student-search filter-field-item">
                <div v-if="selectedStudent" class="selected-student-chip">
                  <div class="selected-student-chip__avatar">
                    <feather-icon icon="UserIcon" size="14" />
                  </div>
                  <div class="selected-student-chip__body">
                    <span class="selected-student-chip__name">{{
                      selectedStudent.name
                    }}</span>
                    <span
                      v-if="selectedStudent.grNo"
                      class="meta-tag meta-tag--gr"
                    >
                      {{ selectedStudent.grNo }}
                    </span>
                  </div>
                  <button
                    type="button"
                    class="selected-student-chip__clear"
                    title="Clear"
                    @click="clearFilterStudent"
                  >
                    <feather-icon icon="XIcon" size="13" />
                  </button>
                </div>

                <div
                  v-else
                  class="student-search-box"
                  :class="{ 'student-search-box--active': showStudentDropdown }"
                >
                  <div class="student-search-input-wrap">
                    <feather-icon
                      icon="SearchIcon"
                      size="16"
                      class="student-search-icon"
                    />
                    <b-form-input
                      v-model="studentSearchQuery"
                      class="filter-search-input student-search-input"
                      placeholder="Search student..."
                      @input="onStudentSearchChange"
                      @focus="studentSearchFocused = true"
                      @blur="onStudentSearchBlur"
                    />
                    <button
                      v-if="studentSearchQuery"
                      type="button"
                      class="student-search-clear"
                      @mousedown.prevent
                      @click="clearStudentSearchQuery"
                    >
                      <feather-icon icon="XIcon" size="14" />
                    </button>
                    <b-spinner
                      v-else-if="studentSearchLoading"
                      small
                      variant="primary"
                      class="student-search-spinner"
                    />
                  </div>

                  <transition name="student-search-fade">
                    <div
                      v-if="showStudentDropdown"
                      class="student-search-dropdown"
                    >
                      <div
                        v-if="studentSearchQuery.length < 2"
                        class="student-search-hint"
                      >
                        <feather-icon icon="InfoIcon" size="14" />
                        <span>Type at least 2 characters</span>
                      </div>

                      <template v-else>
                        <div
                          v-if="studentSearchLoading"
                          class="student-search-state"
                        >
                          <b-spinner small variant="primary" />
                          <span>Searching...</span>
                        </div>

                        <div
                          v-else-if="filteredStudentOptions.length === 0"
                          class="student-search-state"
                        >
                          <div class="student-search-state__icon">
                            <feather-icon icon="UserXIcon" size="22" />
                          </div>
                          <strong>No students found</strong>
                          <small>Try another name or GR number</small>
                        </div>

                        <template v-else>
                          <div class="student-search-dropdown__header">
                            <span
                              >{{ filteredStudentOptions.length }} results</span
                            >
                          </div>
                          <div class="student-options">
                            <button
                              v-for="student in filteredStudentOptions"
                              :key="student.id"
                              type="button"
                              class="student-option"
                              @mousedown.prevent
                              @click="selectFilterStudent(student)"
                            >
                              <div class="student-option__avatar">
                                <feather-icon icon="UserIcon" size="14" />
                              </div>
                              <div class="student-option__content">
                                <span class="student-option__name">{{
                                  student.name
                                }}</span>
                                <span class="student-option__meta">
                                  <span v-if="student.className">{{
                                    student.className
                                  }}</span>
                                  <span v-if="student.sectionName">
                                    · {{ student.sectionName }}</span
                                  >
                                </span>
                              </div>
                              <span
                                v-if="student.grNo"
                                class="student-option__gr"
                              >
                                GR {{ student.grNo }}
                              </span>
                            </button>
                          </div>
                        </template>
                      </template>
                    </div>
                  </transition>
                </div>
              </div>

              <v-select
                v-model="filters.status"
                :options="statusOptions"
                :clearable="true"
                :reduce="(o) => o.value"
                label="text"
                placeholder="Status"
                class="filter-control filter-field-item"
                append-to-body
                @input="onFilterChange"
              />

              <flat-pickr
                v-model="filters.leavingDate"
                class="form-control filter-control filter-field-item"
                :config="leavingDateConfig"
                placeholder="Leaving date"
                @on-change="onFilterChange"
              />
            </div>

            <b-button
              v-if="hasActiveFilters"
              class="action-btn-filter filter-clear-btn mx-50"
              v-b-tooltip.hover.top
              title="Clear filters"
              @click="clearFilters"
            >
              <span class="filter-clear-x" aria-hidden="true">×</span>
            </b-button>

            <div class="filter-actions-group d-flex align-items-center ml-auto">
              <b-button
                v-if="rights.edit"
                variant="outline-primary"
                class="action-btn-filter d-flex align-items-center justify-content-center"
                v-b-tooltip.hover.top="'Role Setup'"
                style="min-width: 46px; border-color: #e0d8ff; background: #fff"
                @click="openRoleSetup"
              >
                <feather-icon icon="SettingsIcon" size="18" />
              </b-button>

              <b-button
                variant="outline-primary"
                class="action-btn-filter d-flex align-items-center justify-content-center"
                v-b-tooltip.hover.top="'Retention History'"
                style="min-width: 46px; border-color: #e0d8ff; background: #fff"
                @click="goRetention"
              >
                <feather-icon icon="ArchiveIcon" size="18" />
              </b-button>

              <b-button
                v-if="showNewWithdrawalBtn"
                variant="gradient-primary"
                class="action-btn-filter add-sheet-btn text-nowrap"
                @click="goForm()"
              >
                <feather-icon icon="PlusIcon" class="mr-50" />
                <span>New Withdrawal</span>
              </b-button>
            </div>
          </b-col>
        </b-row>

        <div class="table-scroll-container">
          <div class="table-scrollbar-top" ref="topScrollbar">
            <div class="table-scrollbar-content"></div>
          </div>

          <div class="table-wrapper" ref="tableWrapper" @scroll="syncScroll">
            <b-table
              class="planner-list-table mt-1"
              :items="rows"
              :fields="fields"
              :busy="listLoading"
              show-empty
              :responsive="false"
              hover
              small
            >
              <template #empty>
                <h3 v-if="!listLoading" class="mt-1 mb-1 text-center">
                  No records found
                </h3>
              </template>

              <template #table-busy>
                <tbody>
                  <tr
                    v-for="n in 5"
                    :key="'skeleton-' + n"
                    class="skeleton-row"
                  >
                    <td
                      v-for="(field, index) in fields"
                      :key="'skeleton-cell-' + index"
                      class="skeleton-cell"
                    >
                      <div class="skeleton-loader"></div>
                    </td>
                  </tr>
                </tbody>
              </template>

              <template #cell(sno)="data">
                {{ (currentPage - 1) * perPage + data.index + 1 }}
              </template>

              <template #cell(student)="{ item }">
                <div class="font-weight-bold" style="color: #2d1b69">
                  {{ item.studentName || item.name || "—" }}
                </div>
                <small class="text-muted">
                  GR {{ item.studentGrNo || item.grNo || item.gr || "—" }}
                </small>
              </template>

              <template #cell(className)="{ item }">
                <b-badge variant="light-primary">
                  {{ classLabel(item) }}
                </b-badge>
              </template>

              <template #cell(status)="{ item }">
                <span
                  class="status-pill"
                  :class="'status-pill--' + (item.status || 'draft')"
                >
                  {{ statusLabel(item.status) }}
                </span>
              </template>

              <template #cell(daysOpen)="{ item }">
                {{
                  item.daysOpen != null
                    ? item.daysOpen
                    : daysOpen(item.createdOn || item.initiatedOn)
                }}
              </template>

              <template #cell(assignee)="{ item }">
                <b-badge variant="light-info">
                  {{
                    formatRole(
                      item.currentAssigneeRole || item.assigneeRole || "—"
                    )
                  }}
                </b-badge>
              </template>

              <template #cell(lastEvent)="{ item }">
                <div class="sw-last-event">
                  <div
                    class="text-truncate font-weight-600"
                    style="max-width: 220px; color: #2d1b69"
                    :title="lastEventText(item)"
                  >
                    {{ lastEventText(item) }}
                  </div>
                  <small
                    v-if="lastEventDate(item)"
                    class="text-muted d-block"
                  >
                    {{ lastEventDate(item) }}
                  </small>
                </div>
              </template>

              <template #head(actions)="data">
                <div class="text-center">
                  <span>{{ data.label }}</span>
                </div>
              </template>

              <template #cell(actions)="{ item }">
                <div class="d-flex justify-content-center align-items-center">
                  <b-button
                    variant="outline-primary"
                    v-b-tooltip.hover.top
                    title="View"
                    class="action-btn-outline mr-1"
                    @click="goDetail(item.id)"
                  >
                    <feather-icon icon="EyeIcon" size="16" />
                  </b-button>
                  <b-button
                    v-if="canEditRow(item)"
                    variant="gradient-primary"
                    v-b-tooltip.hover.top
                    :title="editRowTitle(item)"
                    class="action-btn-table"
                    @click="goForm(item.id)"
                  >
                    <feather-icon icon="EditIcon" size="16" />
                  </b-button>
                </div>
              </template>
            </b-table>
          </div>
        </div>
      </b-card>
    </template>

    <!-- Nested form / detail / retention -->
    <template v-else>
      <withdrawal-form-panel
        v-if="activeView === 'form'"
        :key="'form-' + (selectedId || 'new')"
        :edit-id="selectedId"
        embedded
        @back="goList"
        @saved="onFormSaved"
      />

      <withdrawal-detail-panel
        v-else-if="activeView === 'detail'"
        :key="'detail-' + selectedId"
        :id="selectedId"
        embedded
        @back="goList"
        @edit="goForm"
      />

      <retention-history-panel
        v-else-if="activeView === 'retention'"
        key="retention"
        embedded
        @back="goList"
        @open="goDetail"
      />
    </template>

    <!-- Role Setup Sidebar -->
    <b-sidebar
      id="withdrawal-role-setup"
      v-model="roleSetupVisible"
      bg-variant="white"
      sidebar-class="sidebar-lg"
      right
      width="480px"
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <div>
          <h5 class="mb-0">Withdrawal Role Setup</h5>
          <small class="text-muted">Assign users per campus</small>
        </div>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="roleSetupVisible = false"
        />
      </div>

      <div class="p-2">
        <div v-if="usersLoading || roleSetupLoading" class="text-center py-3">
          <b-spinner variant="primary" small />
          <div class="text-muted small mt-1">
            {{ roleSetupLoading ? "Loading role setup..." : "Loading users..." }}
          </div>
        </div>

        <template v-else>
          <b-form-group
            v-if="showHeadOfficeRoleField"
            label="Head Office"
            label-class="font-weight-bold"
          >
            <v-select
              :key="'ho-' + headOfficeUserOptions.length"
              v-model="roleForm.headOfficeUserID"
              :options="headOfficeUserOptions"
              :reduce="(u) => u.id"
              label="label"
              :clearable="true"
              placeholder="Select head office user"
              class="role-user-select"
            >
              <template #option="option">
                <div class="d-flex align-items-center">
                  <b-avatar
                    size="32"
                    :src="getAvatarPreviewUrl(option.avatar)"
                    variant="light-primary"
                    referrerpolicy="no-referrer"
                  />
                  <div class="ml-1">
                    <div class="font-weight-bold text-primary text-nowrap">
                      {{ option.name }}
                    </div>
                    <small class="text-muted">{{ option.username }}</small>
                  </div>
                </div>
              </template>
              <template #selected-option="option">
                <div class="d-flex align-items-center">
                  <b-avatar
                    size="28"
                    :src="getAvatarPreviewUrl(option.avatar)"
                    variant="light-primary"
                    referrerpolicy="no-referrer"
                  />
                  <div class="ml-50">
                    <div class="font-weight-bold text-primary text-nowrap">
                      {{ option.name }}
                    </div>
                    <small class="text-muted">{{ option.username }}</small>
                  </div>
                </div>
              </template>
            </v-select>
            <small class="text-muted d-block mt-25">
              Head Office users are loaded from Campus {{ headOfficeCampusId }}.
            </small>
          </b-form-group>

          <b-form-group label="Admission Officer" label-class="font-weight-bold">
            <v-select
              :key="'ao-' + userOptions.length"
              v-model="roleForm.admissionOfficerUserID"
              :options="userOptions"
              :reduce="(u) => u.id"
              label="label"
              :clearable="true"
              placeholder="Select admission officer"
              class="role-user-select"
            >
              <template #option="option">
                <div class="d-flex align-items-center">
                  <b-avatar
                    size="32"
                    :src="getAvatarPreviewUrl(option.avatar)"
                    variant="light-primary"
                    referrerpolicy="no-referrer"
                  />
                  <div class="ml-1">
                    <div class="font-weight-bold text-primary text-nowrap">
                      {{ option.name }}
                    </div>
                    <small class="text-muted">{{ option.username }}</small>
                  </div>
                </div>
              </template>
              <template #selected-option="option">
                <div class="d-flex align-items-center">
                  <b-avatar
                    size="28"
                    :src="getAvatarPreviewUrl(option.avatar)"
                    variant="light-primary"
                    referrerpolicy="no-referrer"
                  />
                  <div class="ml-50">
                    <div class="font-weight-bold text-primary text-nowrap">
                      {{ option.name }}
                    </div>
                    <small class="text-muted">{{ option.username }}</small>
                  </div>
                </div>
              </template>
            </v-select>
          </b-form-group>

          <b-form-group label="Branch Head" label-class="font-weight-bold">
            <v-select
              :key="'bh-' + userOptions.length"
              v-model="roleForm.branchHeadUserID"
              :options="userOptions"
              :reduce="(u) => u.id"
              label="label"
              :clearable="true"
              placeholder="Select branch head"
              class="role-user-select"
            >
              <template #option="option">
                <div class="d-flex align-items-center">
                  <b-avatar
                    size="32"
                    :src="getAvatarPreviewUrl(option.avatar)"
                    variant="light-primary"
                    referrerpolicy="no-referrer"
                  />
                  <div class="ml-1">
                    <div class="font-weight-bold text-primary text-nowrap">
                      {{ option.name }}
                    </div>
                    <small class="text-muted">{{ option.username }}</small>
                  </div>
                </div>
              </template>
              <template #selected-option="option">
                <div class="d-flex align-items-center">
                  <b-avatar
                    size="28"
                    :src="getAvatarPreviewUrl(option.avatar)"
                    variant="light-primary"
                    referrerpolicy="no-referrer"
                  />
                  <div class="ml-50">
                    <div class="font-weight-bold text-primary text-nowrap">
                      {{ option.name }}
                    </div>
                    <small class="text-muted">{{ option.username }}</small>
                  </div>
                </div>
              </template>
            </v-select>
          </b-form-group>

          <b-form-group label="Finance Officer" label-class="font-weight-bold">
            <v-select
              :key="'fo-' + userOptions.length"
              v-model="roleForm.financeOfficerUserID"
              :options="userOptions"
              :reduce="(u) => u.id"
              label="label"
              :clearable="true"
              placeholder="Select finance officer"
              class="role-user-select"
            >
              <template #option="option">
                <div class="d-flex align-items-center">
                  <b-avatar
                    size="32"
                    :src="getAvatarPreviewUrl(option.avatar)"
                    variant="light-primary"
                    referrerpolicy="no-referrer"
                  />
                  <div class="ml-1">
                    <div class="font-weight-bold text-primary text-nowrap">
                      {{ option.name }}
                    </div>
                    <small class="text-muted">{{ option.username }}</small>
                  </div>
                </div>
              </template>
              <template #selected-option="option">
                <div class="d-flex align-items-center">
                  <b-avatar
                    size="28"
                    :src="getAvatarPreviewUrl(option.avatar)"
                    variant="light-primary"
                    referrerpolicy="no-referrer"
                  />
                  <div class="ml-50">
                    <div class="font-weight-bold text-primary text-nowrap">
                      {{ option.name }}
                    </div>
                    <small class="text-muted">{{ option.username }}</small>
                  </div>
                </div>
              </template>
            </v-select>
          </b-form-group>

          <small v-if="!userOptions.length" class="text-danger d-block mb-1">
            No users loaded. Check Users/LoadUsers API.
          </small>

          <div class="d-flex justify-content-end align-items-center mt-2">
            <b-button
              variant="outline-secondary"
              class="mr-50"
              :disabled="roleSaving"
              @click="roleSetupVisible = false"
            >
              Cancel
            </b-button>
            <b-button
              variant="gradient-primary"
              :disabled="roleSaving"
              @click="saveRoles"
            >
              <b-spinner v-if="roleSaving" small class="mr-50" />
              Save Setup
            </b-button>
          </div>
        </template>
      </div>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BFormInput,
  BFormGroup,
  BBadge,
  BPagination,
  BTable,
  BSidebar,
  BSpinner,
  BAvatar,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { mapActions } from "vuex";
import { getMediaPreviewUrl } from "@/utils/gdriveUpload";
import {
  withdrawalMixin,
  STATUS_META,
  daysOpen,
  emptyRoleSetup,
  isParentInitiatedWithdrawal,
} from "./withdrawalShared";
import WithdrawalFormPanel from "./withdrawalForm.vue";
import WithdrawalDetailPanel from "./withdrawalDetail.vue";
import RetentionHistoryPanel from "./retentionHistory.vue";

const WITHDRAWAL_RIGHTS_BASE = "student-withdrawal";

export default {
  name: "WithdrawalInbox",
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BFormInput,
    BFormGroup,
    BBadge,
    BPagination,
    BTable,
    BSidebar,
    BSpinner,
    BAvatar,
    vSelect,
    flatPickr,
    WithdrawalFormPanel,
    WithdrawalDetailPanel,
    RetentionHistoryPanel,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  mixins: [withdrawalMixin],
  data() {
    return {
      rights: {},
      listLoading: false,
      activeView: "list",
      selectedId: null,
      rows: [],
      totalCount: 0,
      currentPage: 1,
      perPage: 20,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
      ],
      filters: {
        classID: null,
        sID: null,
        status: null,
        leavingDate: "",
      },
      leavingDateConfig: {
        dateFormat: "Y-m-d",
        allowInput: true,
      },
      statusOptions: [
        { text: "All", value: null },
        ...Object.keys(STATUS_META).map((k) => ({
          text: STATUS_META[k].label,
          value: k,
        })),
      ],
      fields: [
        { key: "sno", label: "#" },
        { key: "student", label: "Student" },
        { key: "className", label: "Class" },
        { key: "status", label: "Status" },
        { key: "daysOpen", label: "Days open" },
        { key: "assignee", label: "Assignee" },
        { key: "lastEvent", label: "Last event" },
        { key: "actions", label: "Actions" },
      ],
      roleSetupVisible: false,
      usersLoading: false,
      roleSaving: false,
      roleSetupLoading: false,
      userOptions: [],
      headOfficeUserOptions: [],
      headOfficeObj: null,
      roleForm: {
        headOfficeUserID: null,
        admissionOfficerUserID: null,
        branchHeadUserID: null,
        financeOfficerUserID: null,
      },
      classesData: [],
      classOptions: [],
      selectedStudent: null,
      studentSearchQuery: "",
      studentSearchFocused: false,
      filteredStudentOptions: [],
      studentSearchLoading: false,
      studentSearchCache: {},
      studentSearchTimer: null,
      lastStudentSearchToken: 0,
    };
  },
  computed: {
    hasActiveFilters() {
      return !!(
        this.filters.classID ||
        this.filters.sID ||
        this.filters.status ||
        this.filters.leavingDate ||
        this.selectedStudent
      );
    },
    displayedRange() {
      if (!this.totalCount) return { start: 0, end: 0 };
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
    showNewWithdrawalBtn() {
      return !!(
        this.rights &&
        this.rights.add &&
        this.canCreateWithdrawal
      );
    },
    showStudentDropdown() {
      return this.studentSearchFocused && !this.selectedStudent;
    },
    headOfficeCampusId() {
      if (!this.headOfficeObj) return 1;
      const id =
        this.headOfficeObj.campusID ??
        this.headOfficeObj.valueNumber ??
        this.headOfficeObj.value ??
        this.headOfficeObj.valueInt;
      return id != null && id !== "" ? Number(id) : 1;
    },
    showHeadOfficeRoleField() {
      return true;
    },
  },
  async created() {
    const pageRights = this.initWithdrawalRights();
    if (!pageRights || !pageRights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = pageRights;

    try {
      await this.loadRoleSetup();
    } catch (e) {
      this.toastErr("Failed to load role setup");
    }
    this.loadClasses();
    this.syncFromRoute();
    if (!this.checkSubRouteRights()) return;
    if (this.activeView === "list") this.reload(true);
  },
  beforeDestroy() {
    if (this.studentSearchTimer) clearTimeout(this.studentSearchTimer);
  },
  mounted() {
    this.$nextTick(() => this.updateScrollbarWidth());
  },
  updated() {
    this.$nextTick(() => this.updateScrollbarWidth());
  },
  watch: {
    $route() {
      this.syncFromRoute();
      if (!this.checkSubRouteRights()) return;
      if (this.activeView === "list") this.reload(true);
    },
  },
  methods: {
    ...mapActions({ get: "get", post: "post", getString: "getString" }),
    initWithdrawalRights() {
      const right = { ...(this.$store.state.rights || {}) };
      const menu = this.$store.state.menu;
      if (Array.isArray(menu)) {
        menu.forEach((el) => {
          (el.children || []).forEach((ch) => {
            if (!ch || !ch.route) return;
            right[ch.route] = {
              view: ch.view,
              add: ch.add,
              edit: ch.edit,
              delete: ch.delete,
            };
          });
        });
      }
      this.$store.commit("setRights", right);
      return this.$store.state.rights[WITHDRAWAL_RIGHTS_BASE];
    },
    daysOpen,
    formatRole(role) {
      if (!role) return "—";
      return String(role)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    formatEventType(type) {
      return String(type || "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    lastEventObj(item) {
      const ev = item && (item.lastTimelineEvent || item.lastEvent);
      if (ev && typeof ev === "object") return ev;
      return null;
    },
    lastEventText(item) {
      const ev = this.lastEventObj(item);
      if (ev) {
        return (
          ev.note ||
          this.formatEventType(ev.eventType) ||
          "—"
        );
      }
      if (typeof item.lastEvent === "string" && item.lastEvent) {
        return item.lastEvent;
      }
      if (typeof item.lastNote === "string" && item.lastNote) {
        return item.lastNote;
      }
      return "—";
    },
    lastEventDate(item) {
      const ev = this.lastEventObj(item);
      const raw =
        (ev && (ev.createdOn || ev.date)) ||
        item.updatedOn ||
        item.initiatedOn ||
        "";
      if (!raw) return "";
      try {
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return "";
        return d.toLocaleString(undefined, {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (e) {
        return "";
      }
    },
    classLabel(item) {
      if (!item) return "—";
      if (item.className || item.class) {
        const cls = item.className || item.class;
        const sec = item.sectionName || item.section || "";
        return sec ? `${cls} - ${sec}` : cls;
      }
      if (!item.classID) return "—";
      const pack = (this.classesData || []).find((el) => {
        const c = el.cls || el;
        return Number(c.id) === Number(item.classID);
      });
      if (!pack) return `Class ${item.classID}`;
      const c = pack.cls || pack;
      const cName = c.name || c.className || c.title || `Class ${item.classID}`;
      const sections = pack.sections || [];
      const sec = sections.find(
        (s) => Number(s.id) === Number(item.sectionID)
      );
      const sName = sec
        ? sec.name || sec.sectionName || sec.title || ""
        : "";
      return sName ? `${cName} - ${sName}` : cName;
    },
    async loadClasses() {
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&dID=0",
          token: this.$store.state.userData.token,
        });
        const data = Array.isArray(res)
          ? res
          : res && Array.isArray(res.data)
          ? res.data
          : [];
        this.classesData = data;
        this.classOptions = data
          .map((el) => el.cls || el)
          .filter((cls) => cls && cls.id)
          .map((cls) => ({
            id: cls.id,
            name: cls.name || cls.className || cls.title || `Class ${cls.id}`,
          }));
      } catch (e) {
        console.error("Load classes error:", e);
        this.classesData = [];
        this.classOptions = [];
      }
    },
    onFilterClassChange() {
      this.resetStudentSearchState(true);
      this.onFilterChange();
    },
    onStudentSearchChange() {
      if (this.studentSearchTimer) clearTimeout(this.studentSearchTimer);
      this.studentSearchTimer = setTimeout(() => {
        this.performStudentSearch();
      }, 700);
    },
    onStudentSearchBlur() {
      setTimeout(() => {
        this.studentSearchFocused = false;
      }, 180);
    },
    clearStudentSearchQuery() {
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
    },
    resetStudentSearchState(clearSelected) {
      if (clearSelected) {
        this.selectedStudent = null;
        this.filters.sID = null;
      } else if (
        this.selectedStudent &&
        this.filters.classID &&
        !this.matchesClassFilter(this.selectedStudent)
      ) {
        this.selectedStudent = null;
        this.filters.sID = null;
      }
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
      this.studentSearchCache = {};
      if (this.studentSearchTimer) {
        clearTimeout(this.studentSearchTimer);
        this.studentSearchTimer = null;
      }
    },
    matchesClassFilter(student) {
      if (!student || !this.filters.classID) return true;
      const studentClassID =
        student.classID ?? student.clsID ?? student.classId ?? null;
      if (studentClassID != null) {
        return Number(studentClassID) === Number(this.filters.classID);
      }
      const selected = this.classOptions.find(
        (c) => Number(c.id) === Number(this.filters.classID)
      );
      const selectedName = ((selected && selected.name) || "")
        .trim()
        .toLowerCase();
      const studentClassName = (student.className || "").trim().toLowerCase();
      if (!selectedName || !studentClassName) return true;
      return studentClassName === selectedName;
    },
    mapStudentSearchOption(student) {
      let className = "";
      let sectionName = "";
      if (student.meta_class) {
        const parts = String(student.meta_class).split(" - ");
        className = (parts[0] || "").trim();
        sectionName = (parts[1] || "").trim();
      } else {
        className = student.className || student.cls || "";
        sectionName = student.sectionName || student.section || "";
      }
      return {
        ...student,
        id: student.id || student.sID,
        name: student.name || "Unknown Student",
        grNo: student.grNo || student.gr || "",
        className,
        sectionName,
        classID:
          student.classID ||
          student.classId ||
          student.class_current ||
          student.class_admitted ||
          (student.sections && student.sections.classID) ||
          null,
      };
    },
    async performStudentSearch() {
      const term = (this.studentSearchQuery || "").trim().toLowerCase();
      if (term.length < 2) {
        this.filteredStudentOptions = [];
        return;
      }
      const cacheKey = `${this.filters.classID || 0}|${term}`;
      if (this.studentSearchCache[cacheKey]) {
        this.filteredStudentOptions = this.studentSearchCache[cacheKey];
        return;
      }

      const token = ++this.lastStudentSearchToken;
      this.studentSearchLoading = true;
      try {
        let url =
          this.$store.state.domain +
          "Students/Search?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&keyword=" +
          encodeURIComponent(this.studentSearchQuery) +
          "&type=";
        if (this.filters.classID) {
          url += "&classID=" + this.filters.classID;
        }

        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
        });
        if (token !== this.lastStudentSearchToken) return;

        let studentsData = [];
        if (response && Array.isArray(response.data)) studentsData = response.data;
        else if (Array.isArray(response)) studentsData = response;

        const mapped = studentsData
          .map((s) => this.mapStudentSearchOption(s))
          .filter((s) => this.matchesClassFilter(s));
        this.filteredStudentOptions = mapped;
        this.studentSearchCache[cacheKey] = mapped;
      } catch (e) {
        console.error("Student search error:", e);
        if (token === this.lastStudentSearchToken) {
          this.filteredStudentOptions = [];
        }
      } finally {
        if (token === this.lastStudentSearchToken) {
          this.studentSearchLoading = false;
        }
      }
    },
    selectFilterStudent(student) {
      this.selectedStudent = { ...student };
      this.filters.sID = student.id || student.sID || null;
      if (!this.filters.classID && student.classID) {
        this.filters.classID = Number(student.classID);
      }
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
      this.studentSearchFocused = false;
      this.reload(true);
    },
    clearFilterStudent() {
      this.selectedStudent = null;
      this.filters.sID = null;
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
      this.studentSearchFocused = false;
      this.reload(true);
    },
    checkSubRouteRights() {
      const name = this.$route.name;
      if (name === "student-withdrawal-form") {
        const id = this.$route.query.id;
        if (id && !this.rights.edit) {
          this.$router.replace({ name: "misc-not-authorized" });
          return false;
        }
        if (!id && (!this.rights.add || !this.canCreateWithdrawal)) {
          this.$router.replace({ name: "misc-not-authorized" });
          return false;
        }
      }
      return true;
    },
    canEditRow(row) {
      if (!this.rights || !this.rights.edit) return false;
      if (!this.hasWithdrawalRole("admission_officer")) return false;

      const parentInitiated = isParentInitiatedWithdrawal(row);
      const status = String(row.status || "");

      if (row.permissions && typeof row.permissions.canEditForm === "boolean") {
        if (row.permissions.canEditForm) return true;
        if (parentInitiated && status === "pending_admission") return true;
        return false;
      }

      return status === "draft" || status === "pending_admission";
    },
    editRowTitle(row) {
      return isParentInitiatedWithdrawal(row)
        ? "Review parent withdrawal request"
        : "Edit";
    },
    syncFromRoute() {
      const name = this.$route.name;
      if (name === "student-withdrawal-form") {
        this.activeView = "form";
        this.selectedId = this.$route.query.id || null;
      } else if (name === "student-withdrawal-detail") {
        this.activeView = "detail";
        this.selectedId = this.$route.params.id || null;
      } else if (name === "student-retention-history") {
        this.activeView = "retention";
        this.selectedId = null;
      } else {
        this.activeView = "list";
        this.selectedId = null;
      }
    },
    pushView(name, params = {}, query = {}) {
      if (this.$route.name !== name) {
        this.$router.push({ name, params, query }).catch(() => {});
      } else {
        this.syncFromRoute();
      }
    },
    goList() {
      this.pushView("student-withdrawal");
    },
    goForm(id) {
      this.selectedId = id || null;
      this.activeView = "form";
      this.pushView(
        "student-withdrawal-form",
        {},
        id ? { id: String(id) } : {}
      );
    },
    goDetail(id) {
      this.selectedId = id;
      this.activeView = "detail";
      this.pushView("student-withdrawal-detail", { id: String(id) });
    },
    goRetention() {
      this.activeView = "retention";
      this.pushView("student-retention-history");
    },
    onFormSaved(id) {
      if (id) this.goDetail(id);
      else this.goList();
    },
    onFilterChange() {
      this.reload(true);
    },
    onPageSizeChange() {
      this.reload(true);
    },
    clearFilters() {
      this.filters.classID = null;
      this.filters.sID = null;
      this.filters.status = null;
      this.filters.leavingDate = "";
      this.selectedStudent = null;
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
      this.studentSearchCache = {};
      this.reload(true);
    },
    async openRoleSetup() {
      this.roleSetupVisible = true;
      this.roleSetupLoading = true;
      try {
        if (!this.headOfficeObj) {
          await this.loadHeadOfficeSettings();
        }
        await this.loadRoleSetup();
        this.applyRoleFormFromSetup(this.roleSetup);
        await Promise.all([
          this.loadUsersForCampus(
            Number(this.$store.state.userData.cId),
            "userOptions"
          ),
          this.loadUsersForCampus(this.headOfficeCampusId, "headOfficeUserOptions"),
        ]);
      } catch (e) {
        console.error(e);
        this.toastErr("Failed to open role setup");
      } finally {
        this.roleSetupLoading = false;
      }
    },
    applyRoleFormFromSetup(source) {
      const setup = source || this.roleSetup || emptyRoleSetup();
      const firstId = (arr) =>
        Array.isArray(arr) && arr.length ? Number(arr[0]) : null;
      this.roleForm = {
        headOfficeUserID: firstId(setup.headOfficeUserIDs),
        admissionOfficerUserID: firstId(setup.admissionOfficerUserIDs),
        branchHeadUserID: firstId(setup.branchHeadUserIDs),
        financeOfficerUserID: firstId(setup.financeOfficerUserIDs),
      };
    },
    async loadHeadOfficeSettings() {
      try {
        const res = await this.post({
          url:
            this.$store.state.domain +
            "Settings/getSettingsAll?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          body: ["head_office"],
          context: this,
          token: this.$store.state.userData.token,
          subjects: true,
        });
        this.headOfficeObj = Array.isArray(res)
          ? res.find((el) => el.key === "head_office")
          : null;
      } catch (e) {
        console.error("Load head office settings error:", e);
        this.headOfficeObj = null;
      }
    },
    async loadUsersForCampus(campusID, targetKey) {
      const envelope = await this.getString({
        url:
          this.$store.state.domain +
          "Users/LoadUsers?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          campusID,
        token: this.$store.state.userData.token,
      });

      const rawList = Array.isArray(envelope)
        ? envelope
        : envelope && Array.isArray(envelope.data)
        ? envelope.data
        : [];

      const mapped = rawList.map((user, index) => {
        const userId =
          user.id != null
            ? Number(user.id)
            : user.userID != null
            ? Number(user.userID)
            : index;
        const name = String(
          user.name || user.username || `User ${userId}`
        ).trim();
        const username = String(user.username || "").trim();
        return {
          id: userId,
          name,
          username,
          avatar: user.avatar || "",
          label: `${name} ${username}`.trim(),
        };
      });

      this.$set(this, targetKey, mapped);
    },
    async loadUsers() {
      this.usersLoading = true;
      try {
        await this.loadUsersForCampus(
          Number(this.$store.state.userData.cId),
          "userOptions"
        );
      } catch (e) {
        console.error("LoadUsers error:", e);
        this.$set(this, "userOptions", []);
        this.toastErr("Failed to load users");
      } finally {
        this.usersLoading = false;
      }
    },
    getAvatarPreviewUrl(url) {
      if (!url) return "";
      if (String(url).startsWith("blob:")) return url;
      return getMediaPreviewUrl(url, "Image");
    },
    async saveRoles() {
      this.roleSaving = true;
      try {
        const toArr = (id) =>
          id != null && id !== "" ? [Number(id)] : [];
        await this.saveRoleSetup({
          headOfficeUserIDs: toArr(this.roleForm.headOfficeUserID),
          admissionOfficerUserIDs: toArr(this.roleForm.admissionOfficerUserID),
          branchHeadUserIDs: toArr(this.roleForm.branchHeadUserID),
          financeOfficerUserIDs: toArr(this.roleForm.financeOfficerUserID),
        });
        this.toastOk("Role setup saved");
        this.roleSetupVisible = false;
      } catch (e) {
        console.error(e);
        this.toastErr("Failed to save role setup");
      } finally {
        this.roleSaving = false;
      }
    },
    syncScroll() {
      if (this.$refs.topScrollbar && this.$refs.tableWrapper) {
        this.$refs.topScrollbar.scrollLeft = this.$refs.tableWrapper.scrollLeft;
      }
    },
    updateScrollbarWidth() {
      const wrapper = this.$refs.tableWrapper;
      const content = this.$el.querySelector(".table-scrollbar-content");
      if (wrapper && content) {
        content.style.width = `${wrapper.scrollWidth}px`;
      }
      const top = this.$refs.topScrollbar;
      if (top && !top._bound) {
        top.addEventListener("scroll", () => {
          if (this.$refs.tableWrapper) {
            this.$refs.tableWrapper.scrollLeft = top.scrollLeft;
          }
        });
        top._bound = true;
      }
    },
    async reload(resetPage) {
      if (resetPage) this.currentPage = 1;
      this.listLoading = true;
      try {
        const extra = {
          pageNumber: this.currentPage,
          pageSize: this.perPage,
        };
        if (this.filters.classID) extra.classID = this.filters.classID;
        if (this.filters.sID) extra.sID = this.filters.sID;
        if (this.filters.status) extra.status = this.filters.status;
        if (this.filters.leavingDate) {
          extra.leavingDate = String(this.filters.leavingDate).slice(0, 10);
        }
        const res = await this.withdrawalGet("GetByPagination", extra);
        const { items, total } = this.extractList(res);
        this.rows = items;
        this.totalCount = total;
        this.$nextTick(() => this.updateScrollbarWidth());
      } catch (e) {
        console.error(e);
        this.rows = [];
        this.totalCount = 0;
        this.toastErr("Failed to load withdrawal inbox");
      } finally {
        this.listLoading = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

/* Role setup sidebar — allow v-select menu to show */
#withdrawal-role-setup.b-sidebar,
#withdrawal-role-setup .b-sidebar-body {
  overflow: visible !important;
}

#withdrawal-role-setup .role-user-select {
  .vs__dropdown-toggle {
    min-height: 48px;
    border: 1px solid #d8d6de;
    border-radius: 0.357rem;
    background: #fff;
    padding: 4px 0 4px 8px;
  }

  .vs__selected {
    display: flex;
    align-items: center;
    margin: 2px 0;
    padding: 0;
  }

  .vs__dropdown-menu {
    z-index: 11000 !important;
    max-height: 260px;
    overflow-y: auto;
    border: 1px solid #d8d6de;
    border-radius: 0.357rem;
    background: #fff;
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
    padding: 0.35rem 0;
  }

  .vs__dropdown-option {
    color: #6e6b7b !important;
    padding: 0.5rem 1rem;
    background: transparent;
    line-height: 1.25;
  }

  .vs__dropdown-option--highlight {
    background: rgba(115, 103, 240, 0.12) !important;
    color: #7367f0 !important;
  }

  .vs__dropdown-option--selected {
    background: transparent;
    color: #7367f0 !important;
  }

  .vs__dropdown-option--selected.vs__dropdown-option--highlight {
    background: rgba(115, 103, 240, 0.12) !important;
    color: #7367f0 !important;
  }
}
</style>

<style lang="scss" scoped>
@import "../planner/plannerListTable.scss";

.filter-fields-group--cols-2,
.filter-fields-group--cols-4 {
  > .filter-field-item,
  > .filter-search-input,
  > .filter-control,
  > .flatpickr-input,
  > input.form-control {
    flex: 1 1 0;
    min-width: 0;
    width: auto !important;
    max-width: none !important;
  }
}

.filter-fields-group--cols-4 {
  gap: 0.5rem;
  flex-wrap: wrap;

  > .filter-student-search {
    flex: 1.4 1 180px;
    min-width: 180px;
  }
}

.filter-student-search {
  position: relative;
}

.student-search-box {
  position: relative;

  &--active .student-search-input-wrap {
    border-color: #7367f0;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12);
  }
}

.student-search-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 40px;
  border: 1.5px solid #e0d8ff;
  border-radius: 10px;
  background: #fff;
}

.student-search-icon {
  position: absolute;
  left: 0.85rem;
  color: #7367f0;
  pointer-events: none;
  z-index: 1;
}

.student-search-input {
  border: none !important;
  box-shadow: none !important;
  height: 38px !important;
  min-height: 38px !important;
  padding-left: 2.15rem !important;
  padding-right: 2rem !important;
  font-size: 0.8rem !important;
  background: transparent !important;

  &:focus {
    box-shadow: none !important;
    border-color: transparent !important;
  }
}

.student-search-clear,
.student-search-spinner {
  position: absolute;
  right: 0.65rem;
  z-index: 2;
}

.student-search-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #f3f2f7;
  color: #6e6b7b;
  cursor: pointer;

  &:hover {
    background: #ffe2e5;
    color: #ea5455;
  }
}

.student-search-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 1060;
  background: #fff;
  border: 1px solid #e0d8ff;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(115, 103, 240, 0.16);
  overflow: hidden;
}

.student-search-dropdown__header {
  padding: 0.55rem 0.85rem;
  background: #faf9ff;
  border-bottom: 1px solid #f0edff;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #8a86b7;
}

.student-search-hint,
.student-search-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 0.85rem;
  color: #6e6b7b;
  font-size: 0.82rem;
  text-align: center;
}

.student-search-state {
  flex-direction: column;

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: #f8f7ff;
    color: #7367f0;
    margin-bottom: 0.25rem;
  }

  strong {
    color: #2d1b69;
    font-size: 0.88rem;
  }

  small {
    color: #8a86b7;
    font-size: 0.75rem;
  }
}

.student-options {
  max-height: 260px;
  overflow-y: auto;
}

.student-option {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  padding: 0.7rem 0.85rem;
  border: none;
  border-bottom: 1px solid #f3f2f7;
  background: #fff;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #faf9ff;
  }

  &:last-child {
    border-bottom: none;
  }

  &__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: rgba(115, 103, 240, 0.12);
    color: #7367f0;
    flex-shrink: 0;
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__name {
    display: block;
    font-size: 0.88rem;
    font-weight: 700;
    color: #2d1b69;
  }

  &__meta {
    display: block;
    font-size: 0.74rem;
    color: #8a86b7;
  }

  &__gr {
    flex-shrink: 0;
    font-size: 0.68rem;
    font-weight: 700;
    color: #7367f0;
    background: #f0edff;
    border-radius: 999px;
    padding: 0.2rem 0.55rem;
  }
}

.selected-student-chip {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  min-height: 40px;
  padding: 0.25rem 0.45rem 0.25rem 0.55rem;
  border: 1.5px solid #e0d8ff;
  border-radius: 10px;
  background: #f8f7ff;

  &__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: #fff;
    color: #7367f0;
  }

  &__body {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }

  &__name {
    font-size: 0.82rem;
    font-weight: 700;
    color: #2d1b69;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 50%;
    background: #fff;
    color: #ea5455;
    cursor: pointer;
  }
}

.meta-tag--gr {
  font-size: 0.68rem;
  font-weight: 700;
  color: #7367f0;
  background: #fff;
  border-radius: 999px;
  padding: 0.1rem 0.45rem;
}

.student-search-fade-enter-active,
.student-search-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.student-search-fade-enter,
.student-search-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.table-wrapper {
  overflow-x: auto;
  width: 100%;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.table-scrollbar-top {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  height: 12px;
  margin-bottom: 8px;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;

  &::-webkit-scrollbar {
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;

    &:hover {
      background: #94a3b8;
    }
  }

  .table-scrollbar-content {
    height: 1px;
    width: 100%;
  }

  @media (max-width: 768px) {
    display: none;
  }
}

.status-pill {
  font-weight: 600;
  padding: 0.4rem 0.7rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  font-size: 0.8rem;

  &--draft {
    color: #6b6578;
    background: #eceaf1;
    border: 1px solid #d5d0de;
  }
  &--pending_branch_head {
    color: #d9480f;
    background: #ffe8cc;
    border: 1px solid #ffc078;
  }
  &--pending_admission {
    color: #1864b0;
    background: #d0ebff;
    border: 1px solid #74c0fc;
  }
  &--pending_finance {
    color: #5f3dc4;
    background: #efe8ff;
    border: 1px solid #b197fc;
  }
  &--pending_head_office {
    color: #364fc7;
    background: #dbe4ff;
    border: 1px solid #91a7ff;
  }
  &--on_hold {
    color: #e67700;
    background: #fff3bf;
    border: 1px solid #ffe066;
  }
  &--rejected {
    color: #c92a2a;
    background: #ffe3e3;
    border: 1px solid #ffa8a8;
  }
  &--completed {
    color: #166534;
    background: #dcfce7;
    border: 1px solid #86efac;
  }
}

.sw-back-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  padding: 0 !important;
  border-radius: 50% !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  border-color: #d9d0ff !important;
  color: #4a4568 !important;
  background: #fff !important;
  flex-shrink: 0;

  &:hover {
    background: #f8f6ff !important;
    border-color: #7367f0 !important;
    color: #7367f0 !important;
  }
}

.action-btn-table {
  width: 36px;
  height: 36px;
  min-width: 36px;
  padding: 0;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
}

.action-btn-outline {
  width: 36px;
  height: 36px;
  min-width: 36px;
  padding: 0;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  background: #ffffff !important;
  border: 2px solid #7367f0 !important;
  color: #7367f0 !important;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &:hover:not(:disabled) {
    background: #f3f0ff !important;
    border-color: #5e50ee !important;
    color: #5e50ee !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.25);
  }
}

.sw-last-event {
  line-height: 1.25;
}

.font-weight-600 {
  font-weight: 600;
}

::v-deep .planner-list-card {
  .gradient-line-wrapper {
    left: 1.15rem;
    width: calc(100% - 2.3rem);
    height: 3px;
    border-radius: 0 0 6px 6px;
  }
}
</style>
